import React, { useState, useEffect, useReducer } from 'react';
import ClientProjectTitle from './projectTitle';
import {
	SdfBox,
	SdfLink,
	SdfQuickStat,
	SdfButton,
	SdfBusyIndicator,
	SdfAlert,
	SdfTooltip,
	SdfIcon
} from '@synerg/react-components';
import { CustomToolTip } from 'shared-one-ux-components';
import { apiUtils } from "shared-one-ux-components";
import ProjectSteps from './projectSteps';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import Toast from '../common/toast';
import { get, uniqueId } from 'lodash';
import { ACCELERATOR_STATUS } from '../constants';

const defaultProjectValues = {
	clientName: 'NA',
	product: 'NA',
	purchaseDate: 'NA',
	clientOOID: 'NA',
	clientProjectDetails: 'NA',
	status: 'NA',
	useCase: 'NA'
};

const defaultCredValues = {
	b2bClientId: null,
	b2bClientSecret: null,
	showB2bSecret: false,
	consumerAppID: null,
	projectID: null,
	dataConnectorID: null,
};

const projectReducer = (state, action) => {
	switch (get(action, 'type', null)) {
		case 'UPDATE':
			return {
				...state, ...action.payload,
			};
		default:
			return state;
	}
};

const clientValReducer = (state, action) => {
	switch (get(action, 'type', null)) {
		case 'UPDATE':
			return {
				...state, ...action.payload,
			};
		default:
			return state;
	}
};

export default function ClientProjectDetails() {
	const [projectDetails, setProjectDetailsDispatch] = useReducer(projectReducer,
		defaultProjectValues);
	const [credValues, setCredValues] = useReducer(clientValReducer,
		defaultCredValues);
	const history = useHistory();
	const location = useLocation();
	const locationToArr = (get(location, 'pathname', [])).split('/');
	const clientOOID = locationToArr[locationToArr.length - 1];
	const acceleratorClientStatus = get(location, 'state', { currClientOOID: '', currClientStatus: '', currClientId: '', currPartnerId: '' });
	const [toast, setToast] = useState(false);
	const [isBusy, setBusy] = useState(false);
	const [hasClientProjectDetails, setHasClientProjectDetails] = useState(false);
	const [loadingClientId, setLoadingClientId] = useState(false);
	const [loadingClientSecret, setLoadingClientSecret] = useState(false);
	const [carDetails, setCarDetails] = useState({});
	const [carDetailsLoading, setCarDetailsLoading] = useState(false);
	const [activeStep, setActiveStep] = useState(0);
	const [fetchError, setFetchError] = useState(false);
	const [hasNewBadge, setNewBadge] = useState(false);
	const [consentInfo, setConsentInfo] = useState({});
	const [status, setStatus] = useState(acceleratorClientStatus.currClientStatus);
	const [sentForApproval, setSentForApproval] = useState(false);
	const isPartnerAppSubscribedOrCreated = status === ACCELERATOR_STATUS.PARTNER_APP_SUBSCRIBED || status === ACCELERATOR_STATUS.CLIENT_APP_CREATED;
	const isClientSubscribedOrComplete = status === ACCELERATOR_STATUS.CLIENT_APP_SUBSCRIBED || status === ACCELERATOR_STATUS.COMPLETE;

	const [scopeLastModified, setScopeLastModified] = useState(null);
	const [filteredApis, setFilteredApis] = useState([]);
	const [events, setEvents] = useState("");
	const [subscribedEvents, setSubscribedEvents] = useState([]);

	const handleResponse = (message, status = 'error') => {
		setToast(
			{ key: uniqueId(), status: status, message: message, showToast: true });
	};
	const fetchSIProjectDetails = async (updated) => {
		try {
			setBusy(true);

			if (clientOOID) {
				const { data, status } = await axios.get(
					`${import.meta.env.VITE_ROOT_PATH}/api/si/project/details`, {
					params: {
						clientOOID,
						clientId: acceleratorClientStatus.currClientId,
						partnerId: acceleratorClientStatus.currPartnerId
					}
				});
				if (status === 200) {
					setProjectDetailsDispatch({
						type: 'UPDATE',
						payload: {
							...data, updated,
							purchaseDate: new Date(data.purchaseDate).toLocaleDateString(
								'en-US', { timeZone: 'America/New_York' }),
						},
					});

					setStatus(get(data, 'status', acceleratorClientStatus.currClientStatus));

					const currClientProjectDetails = get(data, 'clientProjectDetails',
						{});
					if (Object.keys(currClientProjectDetails).length > 0) {
						setHasClientProjectDetails(true);
						setFetchError(false);
					} else {
						setHasClientProjectDetails(false);
					}

				} else {
					handleResponse('Error occurred while fetching project details.');
					setFetchError(true);
				}
			}
		} catch (e) {
			handleResponse('Error occurred while fetching project details.');
			setFetchError(true);
		} finally {
			setBusy(false);
		}
	};

	//create an useffect
	useEffect(() => {
		setNewBadge(acceleratorClientStatus.currClientStatus === ACCELERATOR_STATUS.PARTNER_APP_SUBSCRIBED && acceleratorClientStatus.currClientOOID === clientOOID);
		fetchSIProjectDetails();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (hasClientProjectDetails || sentForApproval) {
			if (!sentForApproval) {
				fetchCreds();
			}

			getCarDetails(projectDetails, clientOOID);
			setConsentInfo(get(projectDetails, 'clientProjectDetails.consentInfo', {}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasClientProjectDetails, sentForApproval]);

	const fetchCreds = async () => {
		const consumerAppID = get(projectDetails,
			'clientProjectDetails.b2bConsumerAppID', '');
		const projectID = get(projectDetails, 'clientProjectDetails._id', '');
		const dataConnectorID = get(projectDetails,
			'clientProjectDetails.dataConnectorID', '');
		const clientOOID = get(projectDetails, 'clientOOID', '');

		if (isPartnerAppSubscribedOrCreated) {
			setCredValues({
				type: 'UPDATE',
				payload: {
					b2bClientId: null,
					b2bClientSecret: null,
					projectID: projectID,
					consumerAppID: consumerAppID,
					dataConnectorID: dataConnectorID,
				},
			});
		} else if (isClientSubscribedOrComplete) {
			await Promise.all([
				getSIClientId(consumerAppID, projectID, clientOOID),
				getSIClientSecret(consumerAppID, projectID, clientOOID)]).then(([clientId, clientSecret]) => {
					if (clientId !== null || clientSecret !== null) {
						setCredValues({
							type: 'UPDATE',
							payload: {
								b2bClientId: clientId,
								b2bClientSecret: clientSecret,
								projectID: projectID,
								consumerAppID: consumerAppID,
								dataConnectorID: dataConnectorID,
							},
						});
					} else {
						setCredValues({
							type: 'UPDATE',
							payload: {
								b2bClientId: null,
								b2bClientSecret: null,
								projectID: projectID,
								consumerAppID: consumerAppID,
								dataConnectorID: dataConnectorID,
							},
						});
					}
				});
		}
	};

	const getSIClientId = async (consumerApplicationID, projectID, clientOOID) => {
		try {
			setLoadingClientId(true);
			const { data: { clientID }, status } = await axios.get(
				`${import.meta.env.VITE_ROOT_PATH}/api/si/project/clientid`,
				{
					params: {
						projectID, consumerApplicationID, clientOOID
					},
				});
			if (status === 200) {
				setLoadingClientId(false);
				return clientID;
			} else {
				handleResponse('Error occurred while fetching ClientID.');
				setLoadingClientId(false);
			}
		} catch (err) {
			handleResponse('Error occurred while fetching ClientID.');
			setLoadingClientId(false);
			return null;
		}
		return null;
	};

	const getSIClientSecret = async (consumerApplicationID, projectID, clientOOID) => {
		try {
			setLoadingClientSecret(true);
			const { data: { clientSecret }, status } = await axios.get(
				`${import.meta.env.VITE_ROOT_PATH}/api/si/project/clientsecret`,
				{
					params: {
						projectID, consumerApplicationID, clientOOID
					},
				});
			if (status === 200) {
				setLoadingClientSecret(false);
				return clientSecret;
			} else {
				handleResponse('Error occurred while fetching client secret.');
				setLoadingClientSecret(false);
			}
		} catch (err) {
			handleResponse('Error occurred while fetching client secret.');
			setLoadingClientSecret(false);
			return null;
		}
		return null;
	};

	const regenerateSIClientAppCreds = async (
		consumerApplicationID, projectID, clientOOID) => {
		try {
			setLoadingClientSecret(true);
			const { data: { clientSecret }, status } = await axios.post(
				`${import.meta.env.VITE_ROOT_PATH}/api/si/project/credentials/regenerate`,
				{ projectID, consumerApplicationID, clientOOID });

			if (status === 200) {
				setLoadingClientSecret(false);
				setCredValues(
					{ type: 'UPDATE', payload: { b2bClientSecret: clientSecret } });
				handleResponse('Successfully regenerated client secret.', 'success');
			} else {
				setLoadingClientSecret(false);
				handleResponse(
					'Error occurred while trying to regenerate client secret.');
			}

		} catch (err) {
			handleResponse(
				'Error occurred while trying to regenerate client secret.');
			setLoadingClientSecret(false);
			return null;
		}
		return null;
	};

	const getCarDetails = async (projectDetails, clientOOID) => {
		try {
			setCarDetailsLoading(true);
			const { data, status } = await axios.get(
				`${import.meta.env.VITE_ROOT_PATH}/api/si/project/app/details`, {
				params: {
					projectID: get(projectDetails, 'clientProjectDetails._id', ''),
					clientOOID,
					partnerClientStatus: get(projectDetails, 'status', ''),
				}
			});

			if (status === 200) {
				setCarDetails({ ...data, error: false });
				const clonedData = JSON.parse(JSON.stringify(data));
				const res = apiUtils.formatAPIDetails(clonedData);
				setFilteredApis(res.filteredApis);
				setEvents(res.events);
				setSubscribedEvents(res.subscribedEvents);
				const scopeLastModified = get(data, 'scopeLastModified', null);
				setScopeLastModified(scopeLastModified);
				setCarDetailsLoading(false);
			} else {
				setCarDetails({ error: true });
				setCarDetailsLoading(false);
				handleResponse('Error occurred while fetching scope details.');
			}
		} catch (e) {
			setCarDetails({ error: true });
			setCarDetailsLoading(false);
			handleResponse('Error occurred while fetching scope details.');
		}
	};

	const redirectToHome = () => {
		history.push('/accelerator-clients');
	};

	const copyValues = (value) => {
		navigator.clipboard.writeText(value);
	};

	const onSendForClientApproval = async () => {
		try {
			setBusy(true);
			const { status } = await axios.post(
				`${import.meta.env.VITE_ROOT_PATH}/api/si/project/client/subscribe`,
				{ projectId: get(projectDetails, 'clientProjectDetails._id', '') });

			if (status === 200) {
				handleResponse('Successfully sent for client approval.', 'success');
				await fetchSIProjectDetails();
				setSentForApproval(true);
			} else {
				handleResponse('Error occurred while sending for client approval.');
			}
		} catch (e) {
			handleResponse('Error occurred while sending for client approval.');
		} finally {
			setBusy(false);
		}
	}

	const apiPickerFetchProjectDetails = async () => {
		await fetchSIProjectDetails(true);
		await getCarDetails(projectDetails, clientOOID);
	}

	return (<div className="flex-col mx-24 my-10">
		<div slot="breadcrumb" className="mb-5 font-medium">
			<SdfLink onClick={() => redirectToHome()}>Accelerator
				clients</SdfLink>
			<span> / </span>
			<span className="font-medium"> {projectDetails.clientName} </span>
		</div>

		<div className="flex flex-col justify-content-between mt-6">
			<div className="w-1/2">
				<ClientProjectTitle
					projectDetails={projectDetails}
					hasBadge={hasNewBadge}
				/>
			</div>

			<div className="relative" style={{ bottom: '0.5rem' }}>
				<SdfBox
					className="flex flex-row col-gap-12 items-center mt-4 pb-4"
					variant="underlined"
					style={{ height: '6.25rem' }}
					width="100%"
					spacing="none"
				>
					<SdfQuickStat className={'max-w-1/3'} labelPosition="top"
						label="ADP product" size="sm">
						<CustomToolTip className="text-xl" text={projectDetails.product.name}
							startEllipsisLen={20} width="100%" />
					</SdfQuickStat>
					<SdfQuickStat className="max-w-1/2"
						labelPosition="top"
						label="Purchase date"
						size="sm"
					>
						<CustomToolTip className="text-xl"
							text={projectDetails.purchaseDate}
							startEllipsisLen={18} width="100%" />
					</SdfQuickStat>
					<div className="flex flex-row col-gap-4">
						<SdfQuickStat className="" labelPosition="top"
							label="Client OOID" size="sm">
							<CustomToolTip className="text-xl"
								text={projectDetails.clientOOID}
								startEllipsisLen={18} width="100%" />
						</SdfQuickStat>
						<SdfQuickStat size="2xs">
							<SdfTooltip positionContext="ELEMENT" trigger="click" state="info">
								<SdfButton
									slot="tooltip-target"
									className="mt-4"
									variant="text"
									icon={'action-copy'}
									onClick={() => copyValues(projectDetails.clientOOID)}
								>
									&nbsp;Copy
								</SdfButton>
								Copied
							</SdfTooltip>
						</SdfQuickStat>
					</div>
				</SdfBox>
			</div>
			{!isBusy && <div className="mt-6">
				<div className="flex">
					<div className={`${activeStep === 0 ? 'w-full' : 'w-64 pr-2'}`}>
						<div className="text-heading-06 font-black mt-4 break-words">
							Project Details
						</div>
						<div className="text-md mt-2">
							Make a selection to view credentials and project scope.
						</div>
					</div>
					{activeStep === 1 && !carDetails.error && <div>
						{projectDetails.changeRequests.length > 0 ?
							<SdfAlert status='warning' type="banner" size="sm"
								className="flex-1">
								<span slot="title">

									You cannot make API calls for recently added APIs without client approval
								</span>
								<span className="block">

									Your current integration still works but the most recently added APIs that impact consent changes will need to be pre-approved by the client.
								</span>
								<div
									slot='action' className='flex gap-2'>
									<SdfIcon slot={"after"} icon={"alert-notification"}></SdfIcon>
									<div className='heading-01'>Pending client approval</div>

								</div>

							</SdfAlert>
							:
							<SdfAlert status={consentInfo.status !== 'allow' ? 'warning' : 'success'} type="banner" size="sm"
								className="flex-1">
								<span slot="title">
									{consentInfo.status !== 'allow' ?
										'You cannot make API calls without client approval' :
										'Client approval received'
									}</span>
								<span className="block">
									{consentInfo.status !== 'allow' ?
										'Before you can make a successful API call on behalf of a client, explicit consent must be provided by the client organization.' :
										'You have received client approval to make API calls on behalf of the client organization.'
									}

								</span>
								{consentInfo.status !== 'allow' &&
									<SdfButton
										slot="action"
										disabled={isClientSubscribedOrComplete ? true : null}
										onClick={onSendForClientApproval}
									>
										Send for client approval
									</SdfButton>}
							</SdfAlert>
						}

					</div>}

					{activeStep === 1 && carDetails.error &&
						<SdfAlert status="error" type="banner" size="sm"
							className="flex-1">
							<span slot="title">Error occurred when trying to retrieve scope details.</span>
							<SdfButton
								slot="action"
								onClick={() => getCarDetails(projectDetails, clientOOID)}
								disabled={hasClientProjectDetails ? null : true}
							>
								Try again
							</SdfButton>
						</SdfAlert>}
				</div>
				{!fetchError && <ProjectSteps projectDetails={projectDetails}
					activeStep={activeStep}
					setActiveStep={setActiveStep}
					credValues={credValues}
					setCredValues={setCredValues}
					regenerateSIClientAppCreds={regenerateSIClientAppCreds}
					projectExists={hasClientProjectDetails}
					loadingClientId={loadingClientId}
					loadingClientSecret={loadingClientSecret}
					carDetailsLoading={carDetailsLoading}
					carDetails={carDetails}
					fetchSIProjectDetails={fetchSIProjectDetails}
					{...{ scopeLastModified, events, subscribedEvents, filteredApis, apiPickerFetchProjectDetails }}
				/>}
			</div>}

		</div>
		{isBusy && <SdfBusyIndicator overlay="true" size="lg" />}
		{toast && <Toast
			key={toast.key}
			status={toast.status}
			message={toast.message}
			showToast={toast.showToast}
		/>}
	</div>);
}
